import React from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { formLayout } from 'helpers/formLayoutConfig';
import { SERVICE_FEE_TYPE } from 'constants/index';

// A company's card-origin flag is tri-state on the API: null inherits the
// group, true and false override it. The form holds it as a mode string.
export const CARD_ORIGIN_MODE = {
  OFF: 'off',
  INHERIT: 'inherit',
  ON: 'on'
};

export const toCardOriginMode = flag => {
  if (flag === true) return CARD_ORIGIN_MODE.ON;
  if (flag === false) return CARD_ORIGIN_MODE.OFF;
  return CARD_ORIGIN_MODE.INHERIT;
};

export const toCardOriginFlag = mode =>
  mode === CARD_ORIGIN_MODE.INHERIT ? null : mode === CARD_ORIGIN_MODE.ON;

// isCardOriginEffective mirrors Company.CardOriginPricingEnabled on the API.
export const isCardOriginEffective = (mode, groupCardOrigin) =>
  mode === CARD_ORIGIN_MODE.ON || (mode === CARD_ORIGIN_MODE.INHERIT && groupCardOrigin);

const RATE_ROWS = [
  {
    label: 'Domestic (AU cards)',
    fee: 'stripe_domestic_fee',
    feeType: 'stripe_domestic_fee_type'
  },
  {
    label: 'International',
    fee: 'stripe_international_fee',
    feeType: 'stripe_international_fee_type'
  }
];

export default function CardOriginPricing({ groupCardOrigin, disabled }) {
  const {
    register,
    watch,
    formState: { errors }
  } = useFormContext();
  const mode = watch('card_origin_pricing_mode');
  const effective = isCardOriginEffective(mode, groupCardOrigin);

  const domesticFee = watch('stripe_domestic_fee');
  const internationalFee = watch('stripe_international_fee');
  // International cards normally cost the endpoint more. A rate that is not
  // is allowed, but is usually a typo, so it is flagged without blocking.
  const internationalNotHigher =
    watch('stripe_domestic_fee_type') === watch('stripe_international_fee_type') &&
    Number.isFinite(domesticFee) &&
    Number.isFinite(internationalFee) &&
    domesticFee !== 0 &&
    internationalFee !== 0 &&
    Math.abs(internationalFee) <= Math.abs(domesticFee);

  return (
    <>
      <Form.Group as={Row} className="mb-1">
        <Form.Label
          column
          {...formLayout.label}
          htmlFor="card_origin_pricing_mode"
          className="text-nowrap form-label mb-1"
        >
          Card Origin Pricing
        </Form.Label>
        <Col {...formLayout.col}>
          <Form.Select
            id="card_origin_pricing_mode"
            size="sm"
            className="fs--1"
            disabled={disabled}
            {...register('card_origin_pricing_mode')}
          >
            <option value={CARD_ORIGIN_MODE.OFF}>Off</option>
            <option value={CARD_ORIGIN_MODE.INHERIT}>
              Inherit from group ({groupCardOrigin ? 'On' : 'Off'})
            </option>
            <option value={CARD_ORIGIN_MODE.ON}>On</option>
          </Form.Select>
        </Col>
      </Form.Group>
      {effective && (
        <div className="border rounded p-2 mb-2">
          <p className="fs--1 fw-semi-bold mb-1">Endpoint Rate by card origin</p>
          {mode === CARD_ORIGIN_MODE.INHERIT && (
            <Form.Text className="fs--2 d-block mb-1">
              Enrolment is inherited from the group; the rates belong to this company.
            </Form.Text>
          )}
          {RATE_ROWS.map(row => (
            <Form.Group as={Row} className="mb-1" key={row.fee}>
              <Form.Label
                column
                {...formLayout.label}
                htmlFor={row.fee}
                className="text-nowrap form-label mb-1"
              >
                {row.label}
                <span className="is-required">*</span>
              </Form.Label>
              <Col {...formLayout.col} className="d-flex gap-2 align-items-start">
                <Form.Select
                  size="sm"
                  className="fs--1 w-50"
                  aria-label={`${row.label} fee type`}
                  disabled={disabled}
                  {...register(row.feeType, { setValueAs: value => Number(value) })}
                >
                  {SERVICE_FEE_TYPE.map(option => (
                    <option value={option.value} key={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <div className="w-50">
                  <Form.Control
                    id={row.fee}
                    type="number"
                    step="any"
                    size="sm"
                    className="fs--1"
                    disabled={disabled}
                    isInvalid={!!errors[row.fee]}
                    {...register(row.fee, { valueAsNumber: true })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[row.fee]?.message}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Form.Group>
          ))}
          {internationalNotHigher && (
            <Form.Text className="fs--2 d-block text-warning">
              The international rate is not higher than the domestic rate. Check this is intended.
            </Form.Text>
          )}
        </div>
      )}
    </>
  );
}

CardOriginPricing.propTypes = {
  groupCardOrigin: PropTypes.bool,
  disabled: PropTypes.bool
};
